import { useTranslation } from 'react-i18next';

import { SsoProviderLogo } from '@/components/auth/SsoProviderLogo';
import {
  BUTTON_STYLES,
  ButtonVariant,
  DEFAULT_BUTTON_STYLES,
  DISABLED_BUTTON_STYLE
} from '@/components/generic/Button/button-config';
import { ssoProviders } from '@/configuration/ssoProviders';
import { getTestId } from '@/shared/test-ids';
import { SsoProviderConfig } from '@/types/ssoProvider';

type Props = {
  onProviderClick: (providerId: string) => void | Promise<void>;
  providers?: SsoProviderConfig[];
  disabled?: boolean;
  submittingProviderId?: string | null;
  showDivider?: boolean;
  dividerLabel?: string;
  /** When false, `labelKey` is shown as a tooltip (`title`) instead of under the button. */
  showLabels?: boolean;
  buttonTestIdPrefix?: string;
  className?: string;
};

export const SsoProviderButtons = ({
  onProviderClick,
  providers = ssoProviders,
  disabled = false,
  submittingProviderId = null,
  showDivider = true,
  dividerLabel,
  showLabels = true,
  buttonTestIdPrefix = 'login-sso-button',
  className
}: Props) => {
  const [t] = useTranslation('common');

  if (providers.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {showDivider && dividerLabel && (
        <div className='mb-3 flex items-center gap-3 py-1'>
          <div className='h-px flex-1 bg-blue-powder' />
          <span className='text-sm text-primary-black'>{dividerLabel}</span>
          <div className='h-px flex-1 bg-blue-powder' />
        </div>
      )}
      <div className='flex flex-wrap items-center justify-center gap-3'>
        {providers.map((provider) => {
          const isSubmitting = submittingProviderId === provider.id;
          const isDisabled = disabled || isSubmitting;
          const label = provider.labelKey ? t(provider.labelKey) : undefined;

          return (
            <div key={provider.id} className='flex flex-col items-center gap-3'>
              <button
                type='button'
                aria-label={label ?? provider.id}
                title={showLabels ? undefined : label}
                data-testid={getTestId(buttonTestIdPrefix, provider.id)}
                disabled={isDisabled}
                onClick={() => onProviderClick(provider.id)}
                className={`${DEFAULT_BUTTON_STYLES} ${BUTTON_STYLES[ButtonVariant.SECONDARY]} !h-12 !w-12 !min-w-12 !p-0 !border-secondary-blue-gray ${isDisabled ? DISABLED_BUTTON_STYLE : ''}`}>
                <SsoProviderLogo
                  iconUrl={provider.iconUrl}
                  alt={provider.id}
                  className='h-6 w-6 object-contain'
                />
              </button>
              {showLabels && label && (
                <span className='max-w-[12rem] text-center text-sm text-primary-black'>{label}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
